import React, { useEffect } from 'react'
import type { ReactElement } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Home from './pages/Home'
import Conversation from './pages/Conversation'
import Profile from './pages/Profile'
import Auth from './pages/Auth'
import FlashcardList from './pages/FlashcardList'
import FlashcardReview from './pages/FlashcardReview'
import Progress from './pages/Progress'
import { Pricing } from './pages/Pricing'
import { StudyPlan } from './pages/StudyPlan'
import { LanguageSelection } from './pages/LanguageSelection'
import { Onboarding } from './pages/Onboarding'
import { useUserStore } from './store/userStore'
import { useShouldShowAds } from './hooks/useShouldShowAds'
import { useInitializeLanguage } from './hooks/useLanguage'

// Placeholder components for features not yet implemented

const NotFoundPage = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-gray-600 mb-8">Page not found</p>
      <a
        href="/"
        className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition"
      >
        Go Home
      </a>
    </div>
  </div>
)

function App() {
  const resolvedTheme = useUserStore((state) => state.themePreference || 'light')
  const shouldShowAds = useShouldShowAds()

  // Initialize user's language preference
  useInitializeLanguage()

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (resolvedTheme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    try {
      localStorage.setItem('preferred-theme', resolvedTheme)
    } catch {}
  }, [resolvedTheme])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const body = document.body
    if (!body) return
    body.dataset.ads = shouldShowAds ? 'on' : 'off'
  }, [shouldShowAds])

  return (
    <div className="min-h-screen bg-gradient-app text-[var(--color-text)] transition-colors duration-300">
      <BrowserRouter>
        <AnimatedRoutes />
      </BrowserRouter>
    </div>
  )
}

function AnimatedRoutes() {
  const location = useLocation()

  return (
    <div className="page-transition-viewport">
      <div key={location.pathname} className="page-transition-layer animate-page-transition">
        <Routes location={location}>
          <Route
            path="/"
            element={
              <OnboardingGuard>
                <Home />
              </OnboardingGuard>
            }
          />
          <Route
            path="/conversation"
            element={
              <OnboardingGuard>
                <Conversation />
              </OnboardingGuard>
            }
          />
          <Route
            path="/flashcards"
            element={
              <OnboardingGuard>
                <FlashcardList />
              </OnboardingGuard>
            }
          />
          <Route
            path="/flashcards/review"
            element={
              <OnboardingGuard>
                <FlashcardReview />
              </OnboardingGuard>
            }
          />
          <Route
            path="/progress"
            element={
              <OnboardingGuard>
                <Progress />
              </OnboardingGuard>
            }
          />
          <Route
            path="/study-plan"
            element={
              <OnboardingGuard>
                <StudyPlan />
              </OnboardingGuard>
            }
          />
          <Route
            path="/profile"
            element={
              <OnboardingGuard>
                <Profile />
              </OnboardingGuard>
            }
          />
          <Route
            path="/pricing"
            element={
              <OnboardingGuard>
                <Pricing />
              </OnboardingGuard>
            }
          />
          <Route path="/auth" element={<Auth />} />
          <Route
            path="/language-selection"
            element={
              <OnboardingGuard>
                <LanguageSelection />
              </OnboardingGuard>
            }
          />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </div>
    </div>
  )
}

function OnboardingGuard({ children }: { children: ReactElement }) {
  const user = useUserStore((state) => state.user)
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding)
  const location = useLocation()

  if (user && !hasCompletedOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  return children
}

export default App
